<script lang="ts" setup>
import { computed, ref, watch, nextTick } from 'vue';
import KeyboardShortcutGuide from '@/common/components/molecules/keyboardShortcutGuide/KeyboardShortcutGuide.vue';
import type { ShortcutData } from '@/common/components/molecules/keyboardShortcutGuide/keyboardShortcutGuide.type';
import {
  setup,
  Props,
  Emit,
} from '@/common/components/molecules/filterSearch/filterSearchDropdownList.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  t,
  filterHistoryList,
  getDropdownListTitle,
  getHighlightParts,
  getElapsedTimeSinceHistoryCreated,
  onMouseenter,
  onClickListItem,
  onClickHistoryItem,
  onClickRemoveHistoryItem,
} = setup(props, emit);

const mainListRef = ref<HTMLUListElement>();

watch(
  () => props.dropdownListInfo.filteredItems,
  () => {
    nextTick(() => {
      if (mainListRef.value) {
        mainListRef.value.scrollTop = 0;
      }
    });
  },
);

const guideItems = computed<ShortcutData[]>(() => {
  const { filteredItems, items: dropdownItems, multi } = props.dropdownListInfo;
  const hasFilteredItems = !!filteredItems?.length;
  const hasDropdownItems = !!dropdownItems?.length;

  if (hasFilteredItems) {
    const result: ShortcutData[] = [
      {
        items: ['up', 'down'],
        desc: t('WORD.TO_NAVIGATE', 'To navigate'),
      },
      {
        items: ['enter'],
        desc: multi
          ? t('WORD.TO_SELECT', 'To select')
          : t('WORD.TO_SELECT_AND_COMPLETE', 'To select and complete'),
      },
    ];

    if (multi) {
      result.push({
        items: ['control', '+', 'enter'],
        desc: t('WORD.TO_COMPLETE', 'To complete'),
      });
    }

    return result;
  }

  if (!hasDropdownItems) {
    return [
      {
        items: ['enter'],
        desc: t('WORD.TO_COMPLETE', 'To complete'),
      },
    ];
  }

  return [];
});

const guideSuffixItems = computed<ShortcutData[]>(() => {
  const { filteredItems, items: dropdownItems, isEditing } = props.dropdownListInfo;
  const hasFilteredItems = !!filteredItems?.length;
  const hasDropdownItems = !!dropdownItems?.length;
  const isVisible = hasFilteredItems || !hasDropdownItems;

  if (!isVisible) {
    return [];
  }

  return [
    {
      items: ['esc'],
      desc: isEditing ? t('WORD.TO_CANCEL', 'To cancel') : t('WORD.TO_CLOSE', 'Close'),
    },
  ];
});
</script>

<template>
  <div class="filter-search-dropdown-list">
    <div class="filter-search-dropdown-list__items">
      <div class="filter-search-dropdown-list__sub-title">
        {{ getDropdownListTitle(props.dropdownListInfo.category) }}
      </div>

      <template v-if="!props.dropdownListInfo?.items?.length">
        <ul class="filter-search-dropdown-list__no-data">
          <li>{{ t('WORD.SEARCH', 'Search') }}</li>
        </ul>
      </template>

      <template v-else-if="!props.dropdownListInfo?.filteredItems?.length">
        <ul class="filter-search-dropdown-list__no-data">
          <li>{{ t('MESSAGE.NO_RECORDS', 'No records') }}</li>
        </ul>
      </template>

      <template v-else>
        <ul
          ref="mainListRef"
          class="filter-search-dropdown-list__main-list-wrapper"
        >
          <li
            v-for="(listItem, listIndex) in props.dropdownListInfo.filteredItems"
            :key="listItem.id"
            :class="[
              'filter-search-dropdown-list__item',
              {
                hovered: props.hoveredDropdownItemIndex === listIndex,
                selected: listItem.checked,
                disabled: listItem?.disabled,
              },
            ]"
            @mouseenter="onMouseenter(listIndex)"
            @click="() => onClickListItem(listItem)"
          >
            <span
              v-if="props.dropdownListInfo.multi"
              class="ev-checkbox"
            >
              <input
                v-model="listItem.checked"
                type="checkbox"
                class="ev-checkbox-input"
              />
              <span class="ev-checkbox-label" />
            </span>
            <span class="filter-search-dropdown-list__item-name">
              <template
                v-for="(part, partIndex) in getHighlightParts(listItem.name, props.searchText)"
                :key="partIndex"
              >
                <span
                  v-if="part.isMatch"
                  class="filter-search-dropdown-list__highlight"
                  >{{ part.text }}</span
                >
                <span v-else>{{ part.text }}</span>
              </template>
            </span>
            <span
              v-if="listItem.isDefaultColumn"
              class="filter-search-dropdown-list__default-column-badge"
            >
              {{ t('WORD.DEFAULT_VALUE') }}
            </span>
          </li>
        </ul>
      </template>
    </div>

    <div
      v-if="props.showFilterHistoryList"
      class="filter-search-dropdown-list__history-wrapper"
    >
      <div class="filter-search-dropdown-list__sub-title">
        {{ t('WORD.RECENT_SEARCHES', 'Recent Searches') }}
      </div>

      <ul>
        <li
          v-for="(history, historyIndex) in filterHistoryList"
          :key="`${historyIndex}-${history.token.key}`"
          :class="[
            'filter-search-dropdown-list__item',
            'filter-search-dropdown-list__history-item',
            {
              hovered:
                props.hoveredDropdownItemIndex ===
                props.dropdownListInfo.filteredItems?.length + historyIndex,
            },
          ]"
          @mouseenter="onMouseenter(props.dropdownListInfo.filteredItems?.length + historyIndex)"
          @click="() => onClickHistoryItem(history)"
        >
          <span class="filter-search-dropdown-list__history-icon">
            <ev-icon icon="ev-icon-time-line" />
          </span>
          <span class="filter-search-dropdown-list__history-name">
            {{
              `${history.token.key.name}
               ${history.token.operator?.name ?? ''}
               ${history.token.values?.map(({ name }) => name).join(',')}`
            }}
          </span>
          <span class="filter-search-dropdown-list__history-created-time">
            {{ getElapsedTimeSinceHistoryCreated(history?.createdTime) }}
          </span>
          <span
            class="filter-search-dropdown-list__history-remove-btn"
            @click="(e) => onClickRemoveHistoryItem(e, history)"
          >
            <ev-icon
              size="xsmall"
              icon="icon-close"
            />
          </span>
        </li>
      </ul>
    </div>

    <div class="filter-search-dropdown-list__guide-wrapper">
      <keyboard-shortcut-guide
        v-if="guideItems.length || guideSuffixItems.length"
        :items="guideItems"
        :suffix-items="guideSuffixItems"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.filter-search-dropdown-list {
  --filter-search-dropdown-list-item-padding: 4.5px 12px;

  display: flex;
  flex-direction: column;
  height: 100%;

  &__items {
    flex: auto;
  }

  &__history-wrapper {
    flex-shrink: 0;
    overflow-y: auto;
    border-top: 1px solid var(--color-gray-05-05);
  }

  &__guide-wrapper {
    display: block;
    flex: 30px 0 0;
    padding: 4px 12px;
    cursor: default;
    border-top: 1px solid var(--color-gray-05-05);
  }

  &__main-list-wrapper {
    max-height: 120px;
    overflow-y: auto;
  }

  &__item {
    padding: var(--filter-search-dropdown-list-item-padding);
    cursor: pointer;
    font-size: 13px;
    color: var(--color-gray-01-11);

    .ev-checkbox {
      margin-right: 5px !important;
    }

    &.selected {
      background-color: var(--color-blue-10-00);
    }

    &.hovered,
    &:hover {
      background-color: var(--color-blue-09-01);
    }

    &.disabled {
      cursor: not-allowed;
      background-color: transparent;
      color: var(--color-gray-04-06);
    }
  }

  &__sub-title {
    padding: var(--filter-search-dropdown-list-item-padding);
    padding-top: 6px;
    cursor: default;
    font-size: 11px;
    font-weight: 700;
    color: var(--main-font-color);
  }

  &__no-data {
    padding: var(--filter-search-dropdown-list-item-padding);
    cursor: default;
    font-size: 11px;
    color: var(--color-gray-04-08);
  }

  &__history-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    max-width: 100%;
  }

  &__history-icon {
    flex: 20px 0 0;
    padding-top: 1.5px;
    margin-right: 3px;
    text-align: center;
  }

  &__history-name {
    flex: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__history-created-time {
    flex-grow: 0;
    flex-shrink: 0;
    color: var(--color-gray-04-08);
    text-align: right;
  }

  &__history-remove-btn {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    margin-left: 4px;
    opacity: 0;
    cursor: pointer;

    [class^='icon-'] {
      background-color: var(--color-gray-04-08) !important;
    }

    &:hover {
      [class^='icon-'] {
        background-color: var(--color-gray-01-11) !important;
      }
    }
  }

  &__history-item:hover &__history-remove-btn {
    opacity: 1;
  }

  &__default-column-badge {
    padding: 2px 6px;
    margin-left: 5px;
    border-radius: 4px;
    background-color: var(--color-blue-04);
    font-size: 10px;
    font-weight: 500;
    color: var(--color-gray-08-00);
    white-space: nowrap;
  }

  &__item-name {
    flex: 1;
    min-width: 0;
  }

  &__highlight {
    color: var(--color-blue-05-05);
  }
}
</style>
